import { useField } from "formik";
import {
  Box,
  Text,
  IconButton,
  Button,
  Flex,
  TextArea,
} from "@radix-ui/themes";
import { RichTextEditor } from "./RichTextEditor";
import { cn } from "@/utils/cn";
import { Pencil, Check, X, ChevronDown, ChevronUp } from "lucide-react";
import { useState, useEffect } from "react";

interface RichTextFieldProps {
  readonly name: string;
  readonly label?: string;
  readonly required?: boolean;
  readonly className?: string;
  readonly readonly?: boolean;
  readonly disabled?: boolean;
  readonly description?: string;
  readonly options?: {
    readonly rows?: number;
    readonly enable_markdown?: boolean;
  } | null;
  readonly isEditing?: boolean;
  readonly individualEditAllowed?: boolean;
  readonly onChange?: (value: string) => void;
  readonly onEditSaved?: (name: string, value: string) => void;
  readonly onIndividualEditModeChanged?: (isIndividualEditing: boolean) => void;
}

export default function RichTextField({
  name,
  label,
  required = false,
  className,
  disabled,
  readonly,
  description,
  options,
  isEditing = true,
  individualEditAllowed = false,
  onChange,
  onEditSaved,
  onIndividualEditModeChanged,
}: RichTextFieldProps) {
  const [field, meta, helpers] = useField(name);
  const [isIndividualEditing, setIsIndividualEditing] = useState(false);
  const [tempValue, setTempValue] = useState<string>("");
  const [isExpanded, setIsExpanded] = useState(false);
  const hasError = meta.touched && meta.error;

  useEffect(() => {
    if (field.value !== undefined) {
      setTempValue(field.value);
    }
  }, [field.value]);

  // Reset individual editing state when parent edit mode changes
  useEffect(() => {
    if (isEditing) {
      setIsIndividualEditing(false);
    }
  }, [isEditing]);

  const handleEdit = () => {
    setIsIndividualEditing(true);
    setTempValue(field.value ?? "");
    onIndividualEditModeChanged?.(true);
  };

  const handleSave = () => {
    helpers.setValue(tempValue);
    onChange?.(tempValue);
    onEditSaved?.(name, tempValue);
    setIsIndividualEditing(false);
    onIndividualEditModeChanged?.(false);
  };

  const handleCancel = () => {
    setTempValue(field.value ?? "");
    setIsIndividualEditing(false);
    onIndividualEditModeChanged?.(false);
  };

  const handleChange = (value: string) => {
    if (isIndividualEditing) {
      setTempValue(value);
    } else {
      helpers.setValue(value);
      onChange?.(value);
    }
  };

  // Determine current field state
  const shouldRenderTextOnly = !isEditing && !isIndividualEditing;
  const shouldShowEditIcon =
    !isEditing && individualEditAllowed && !isIndividualEditing;

  // Convert HTML to readable text for display-only mode
  const getDisplayValue = (htmlContent: string) => {
    if (!htmlContent) return "";

    // Handle common HTML elements for better readability
    let text = htmlContent
      // Convert paragraphs to line breaks
      .replace(/<\/p>/gi, "\n")
      .replace(/<p[^>]*>/gi, "")
      // Convert headings to text with spacing
      .replace(/<\/h[1-6]>/gi, "\n\n")
      .replace(/<h[1-6][^>]*>/gi, "")
      // Convert line breaks
      .replace(/<br\s*\/?>/gi, "\n")
      // Convert list items
      .replace(/<\/li>/gi, "\n")
      .replace(/<li[^>]*>/gi, "• ")
      // Remove all other HTML tags
      .replace(/<[^>]*>/g, "")
      // Clean up extra whitespace
      .replace(/\n\s*\n/g, "\n\n")
      .trim();

    // Limit display length for better UX
    if (text.length > 200) {
      text = text.substring(0, 200) + "...";
    }

    return text;
  };

  return (
    <Box className={cn("space-y-1", className)}>
      {label && (
        <div className="flex items-center justify-between">
          <Text as="label" size="2" weight="medium" htmlFor={name}>
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </Text>
        </div>
      )}

      {description && (
        <Text size="1" color="gray">
          {description}
        </Text>
      )}

      {shouldRenderTextOnly ? (
        <Box style={{ position: "relative" }}>
          <TextArea
            readOnly
            resize={isExpanded ? "both" : "vertical"}
            rows={options?.rows || 3}
            value={!field.value ? "<not set>" : getDisplayValue(field.value)}
            style={{
              cursor: individualEditAllowed ? "pointer" : undefined,
              color: !field.value ? "var(--gray-9)" : undefined,
              fontStyle: !field.value ? "italic" : undefined,
              backgroundColor: "var(--color-page-background)",
            }}
            onClick={individualEditAllowed ? handleEdit : undefined}
          />

          {/* Edit icon overlay */}
          {shouldShowEditIcon && (
            <IconButton
              size="1"
              variant="ghost"
              onClick={handleEdit}
              aria-label="Edit"
              style={{
                position: "absolute",
                top: "8px",
                right: "8px",
                backgroundColor: "var(--color-panel-solid)",
                borderRadius: "var(--radius-2)",
                opacity: 0.8,
              }}
            >
              <Pencil className="w-4 h-4" />
            </IconButton>
          )}

          <Flex gap="2" mt="2" align="center">
            {field.value && getDisplayValue(field.value).length > 100 && (
              <Button
                size="1"
                type="button"
                variant="ghost"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-3 h-3 mr-1" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3 mr-1" />
                    Show More
                  </>
                )}
              </Button>
            )}
          </Flex>
        </Box>
      ) : (
        <div>
          <RichTextEditor
            value={isIndividualEditing ? tempValue : field.value ?? ""}
            onChange={handleChange}
            disabled={
              disabled ?? readonly ?? (!isEditing && !isIndividualEditing)
            }
            rows={options?.rows ?? 6}
            enableMarkdown={options?.enable_markdown ?? true}
            placeholder={`Enter ${label?.toLowerCase() ?? "content"}...`}
            className={hasError ? "border-red-500" : ""}
          />

          {isIndividualEditing && !isEditing && (
            <Flex gap="2" mt="2" justify="start">
              <Button
                size="1"
                variant="soft"
                color="red"
                onClick={handleCancel}
              >
                <X className="w-3 h-3 mr-1" />
                Cancel
              </Button>
              <Button
                size="1"
                variant="soft"
                color="green"
                onClick={handleSave}
              >
                <Check className="w-3 h-3 mr-1" />
                Save
              </Button>
            </Flex>
          )}
        </div>
      )}

      {hasError && (
        <Text size="1" color="red">
          {meta.error}
        </Text>
      )}
    </Box>
  );
}
